import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import {
  getUserProfile,
  loginWithEmail,
  startGoogleSignIn,
  completeGoogleSignIn,
  registerWithEmail,
  startAutomaticTeacherTrial,
  logout as authLogout,
  resetPassword,
  updateUserSettings,
} from '../services/authService';
import { subscribeToUserProfile } from '../services/dataService';
import { UserProfile, PLAN_LIMITS } from '../types';
import { getEffectivePlan } from '../services/subscription';
import { trackMetaEvent } from '../services/metaPixel';
import { trackEvent } from '../services/analytics';
import { readStashedReferral, clearStashedReferral } from '../services/referralLink';
import { claimReferralCode } from '../services/referralService';

// Trial expiration is now computed at runtime by getEffectivePlan() rather than
// persisted via a client-side write. Firestore rules block client writes to
// subscription.* fields, and the Stripe webhook (Admin SDK) is the only path
// that persists real conversions. UI gates already use getEffectivePlan().

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  pendingGoogleRole: boolean;
  error: string | null;
  isEnterpriseTeacher: boolean;
  login: (email: string, password: string) => Promise<void>;
  startGoogleLogin: () => Promise<{ needsRole: boolean }>;
  completeGoogleLogin: (role: 'teacher' | 'student') => Promise<void>;
  register: (email: string, password: string, role: 'teacher' | 'student' | 'board_admin', displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  updateSettings: (settings: Partial<UserProfile['settings']>) => Promise<void>;
  canUseFeature: (feature: keyof typeof PLAN_LIMITS.free) => boolean;
  refreshProfile: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingGoogleRole, setPendingGoogleRole] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEnterpriseTeacher, setIsEnterpriseTeacher] = useState(false);

  const refreshProfile = async () => {
    if (currentUser) {
      const profile = await getUserProfile(currentUser.uid);
      setUserProfile(profile);
    }
  };

  /**
   * The last uid this listener SAW signed in.
   *
   * Needed to tell a real sign-out from the initial null that Firebase emits on every page load before
   * it restores the session. Clearing the vault on that initial null would wipe the cached key on every
   * single page load, so the teacher would retype their passphrase every time they opened the app — the
   * precise thing the key cache exists to prevent.
   */
  const lastSignedInUid = useRef<string | null>(null);

  // Auth state — tracks who is logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        lastSignedInUid.current = user.uid;
      } else {
        /**
         * LOCK THE VAULT IN **THIS** TAB TOO.
         *
         * `authService.logout` locks the tab the teacher pressed the button in. Every OTHER tab only
         * learns about the sign-out here — and did nothing about it, so its module-level session kept the
         * DEK handle and every decrypted record for the lifetime of the page. `isUnlocked` stayed true
         * there, so the same teacher signing back in on that tab got an unlocked vault without a
         * passphrase, and any `cacheClassKey` from it repopulated the IndexedDB store the sign-out had
         * just cleared.
         *
         * A shared staff-room browser is the case that makes this matter: tab one is logged out, tab two
         * is still holding thirty children's names in memory.
         *
         * Only on a TRANSITION out of a signed-in state, never on the initial null.
         */
        if (lastSignedInUid.current) {
          lastSignedInUid.current = null;
          void import('../services/vault/vaultManager')
            .then(m => m.lockEverything())
            .catch(err => console.error('vault: could not lock on sign-out in this tab', err));
        }
        setUserProfile(null);
        setPendingGoogleRole(false);
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  // Enterprise teacher check — runs whenever userProfile changes
  // If any associated school has subscriptionStatus === 'active', grant enterprise access
  useEffect(() => {
    const associations = userProfile?.schoolAssociations;
    if (!associations || associations.length === 0) {
      setIsEnterpriseTeacher(false);
      return;
    }
    let cancelled = false;
    Promise.all(
      associations.map(assoc =>
        getDoc(doc(db, 'schools', assoc.schoolId)).then(snap => snap.data())
      )
    ).then(schools => {
      if (!cancelled) {
        setIsEnterpriseTeacher(
          schools.some(s => s?.subscriptionStatus === 'active')
        );
      }
    }).catch(() => {
      if (!cancelled) setIsEnterpriseTeacher(false);
    });
    return () => { cancelled = true; };
  }, [userProfile]);

  // Live profile listener — updates userProfile (including aiUsage) in real time
  useEffect(() => {
    if (!currentUser || currentUser.isAnonymous) {
      if (currentUser?.isAnonymous) setLoading(false);
      return;
    }

    let resolved = false;

    const unsubscribe = subscribeToUserProfile(currentUser.uid, (profile) => {
      resolved = true;
      if (profile) {
        setUserProfile(profile);
        setPendingGoogleRole(false);
        // NOTE: the old client-side schoolRoles-from-schoolAssociations migration was
        // removed — schoolRoles is now client-IMMUTABLE (server-granted only) to close a
        // self-escalation hole. Any legacy users missing schoolRoles are backfilled by a
        // one-time Admin SDK script (scripts/backfill-school-roles.ts).
        // Fire-and-forget: claim class membership for any class this user is
        // listed in by email, AND claim ownership of any pre-staged classes
        // from a CSV-imported teacher record (Tier 4.6). Both endpoints
        // are idempotent server-side. One claim per session each via
        // sessionStorage flags.
        //
        // KEY VERSION: bump the suffix to force a one-time re-claim for EVERY existing
        // session. Needed because the claim now also stamps the student's authUid into
        // the roster (Stage 0.1 / readerUids) — students whose session predates that
        // code have the old flag set and never re-run the claim, so they never get
        // linked. _v2 makes them all re-claim (and get stamped) on their next load.
        if (currentUser.email && !sessionStorage.getItem('claimedClassMembership_v2')) {
          sessionStorage.setItem('claimedClassMembership_v2', '1');
          currentUser.getIdToken().then(idToken => {
            // claim-class-membership: student/member claim by email
            const opts = {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
              },
              body: JSON.stringify({}),
            };
            return Promise.all([
              fetch('/.netlify/functions/claim-class-membership', opts),
              // claim-teacher-classes: pre-staged class ownership claim
              fetch('/.netlify/functions/claim-teacher-classes', opts),
            ]);
          }).catch(err => {
            console.error('Failed to claim memberships (non-fatal):', err);
          });
        }
        /**
         * Retry the Ultra trial grant for a teacher still carrying the marker.
         *
         * This used to live ONLY in the 5-second onSnapshot fallback below,
         * which runs when Firestore has NOT answered — so for every teacher
         * with a working connection it never ran at all. A signup whose
         * start-trial call failed once (a cold function, a dropped request, a
         * closed tab) was therefore stranded on `free` permanently: the marker
         * stayed set, nothing ever looked at it again, and the teacher saw
         * Free's 25-student cap while the app told them they were on Ultra.
         * Only a session slow enough to miss the 5s deadline could rescue them.
         *
         * Guarded per uid per session, and the flag is set only on a TERMINAL
         * outcome, so a transient failure is retried on the next snapshot —
         * the same shape as the enterprise capture below. Re-granting is not a
         * risk: start-trial checks a server-side ledger no client can write
         * before it grants anything.
         */
        /**
         * Apply an invite code for a teacher who ALREADY had an account.
         *
         * The claim used to live only in the signup paths, so it fired for a
         * brand-new account and nowhere else. A colleague who signed up weeks
         * ago, never paid, and then followed an invite link got nothing: they
         * land on /register, cannot register because the email is taken, sign
         * in instead, and the code is silently dropped. That is probably the
         * most common referral there is — you tell someone who already tried
         * the product.
         *
         * Safe to attempt on any load: the server refuses a second code, a
         * code after the first subscription, and a teacher's own code. The
         * local checks only avoid a pointless request.
         */
        const referralKey = `referralClaimTried_${currentUser.uid}`;
        const stashedCode = readStashedReferral();
        if (
          stashedCode
          && profile.role === 'teacher'
          && !profile.referredByUid
          && !profile.subscription?.stripeSubscriptionId
          && !sessionStorage.getItem(referralKey)
        ) {
          sessionStorage.setItem(referralKey, '1');
          claimReferralCode(stashedCode)
            .then(() => clearStashedReferral())
            .catch(err => {
              // A referral that does not attach is a missing bonus, never a
              // broken sign-in.
              console.error('Could not apply the referral code (non-fatal):', err);
            });
        }

        const trialRetryKey = `autoTrialRetried_${currentUser.uid}`;
        if (profile.role === 'teacher' && profile.autoTrialPending && !sessionStorage.getItem(trialRetryKey)) {
          // Set BEFORE the call, synchronously: two snapshots can land before
          // one fetch resolves, and a flag set in .then() would let both through.
          sessionStorage.setItem(trialRetryKey, '1');
          startAutomaticTeacherTrial(currentUser)
            .catch(err => {
              /**
               * The guard STAYS SET on failure, for this tab.
               *
               * Clearing it meant every subsequent snapshot re-fired the call,
               * and a user the server will always refuse (they bought a plan,
               * or already had a trial) generated one request per write to
               * their own document, indefinitely. One attempt per tab is enough
               * to rescue a teacher whose grant genuinely missed; a reload
               * gives another. The server now also consumes the marker when it
               * refuses, so a settled account stops asking altogether.
               */
              console.error('Automatic Ultra trial retry failed (non-fatal):', err);
            });
        }
        // Fire-and-forget: enterprise domain capture. If this user's verified
        // email domain matches a configured enterprise domain and the org has a
        // free seat, the server grants the enterprise plan. The write lands on
        // this same user doc, so the onSnapshot listener above re-fires and the
        // new plan flows in automatically — no manual refresh needed. The flag is
        // keyed by uid so switching accounts in the same tab still triggers the
        // check for the second account (a global flag skipped it before).
        const enterpriseProvisionKey = `enterpriseProvisioned_${currentUser.uid}`;
        // Skip students entirely (they never get a seat). For teachers OR a
        // brand-new signup whose role isn't set yet, run the check. Crucially we
        // set the "done" flag only on a TERMINAL outcome — a fresh Google signup
        // often hits this snapshot before onboarding writes role='teacher', which
        // the server reports as skipped_non_teacher. Setting the flag up front
        // (the old behaviour) meant that early skip was never retried, so the
        // teacher stayed on free. Leaving it unset lets the next profile snapshot
        // (once role='teacher') retry and grant the seat automatically.
        const roleKnownNonTeacher = profile?.role != null && profile.role !== 'teacher';
        if (currentUser.email && !sessionStorage.getItem(enterpriseProvisionKey) && !roleKnownNonTeacher) {
          currentUser.getIdToken()
            .then(idToken => fetch('/.netlify/functions/enterprise-provision', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
              body: JSON.stringify({}),
            }))
            .then(r => r.json().catch(() => ({})))
            .then((res: { status?: string; reason?: string }) => {
              const outcome = res.status || res.reason || '';
              // Transient — the user doc or teacher role isn't in place yet. Leave
              // the flag unset so a later snapshot retries. Everything else is final.
              const transient = outcome === 'no_user' || outcome === 'skipped_non_teacher';
              if (!transient) sessionStorage.setItem(enterpriseProvisionKey, '1');
            })
            .catch(err => {
              console.error('Enterprise provision check failed (non-fatal):', err);
            });
        }
      } else {
        setPendingGoogleRole(true);
        setUserProfile(null);
      }
      setLoading(false);
    });

    // Fallback: if Firestore onSnapshot doesn't respond within 5s (mobile / slow network),
    // do a one-shot getDoc so we don't hang on the loading screen forever.
    const fallback = setTimeout(async () => {
      if (!resolved) {
        const profile = await getUserProfile(currentUser.uid);
        if (profile) {
          setUserProfile(profile);
          setPendingGoogleRole(false);
          // Recovery path for a transient failure between creating a teacher
          // profile and the server-side trial grant. The server consumes the
          // marker atomically on either a trial or enterprise provision.
          if (profile.role === 'teacher' && profile.autoTrialPending) {
            startAutomaticTeacherTrial(currentUser).catch(err => {
              console.error('Automatic Ultra trial retry failed (non-fatal):', err);
            });
          }
        } else {
          setPendingGoogleRole(true);
          setUserProfile(null);
        }
        setLoading(false);
      }
    }, 5000);

    return () => {
      unsubscribe();
      clearTimeout(fallback);
    };
  }, [currentUser]);

  // On mobile, the tab can be suspended while a Google auth popup is open.
  // When the tab becomes visible again, re-check auth state so the app
  // doesn't hang waiting for a Firebase callback that was throttled.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && auth.currentUser && !userProfile) {
        getUserProfile(auth.currentUser.uid).then((profile) => {
          if (profile) {
            setUserProfile(profile);
            setPendingGoogleRole(false);
            setLoading(false);
          }
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userProfile]);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      const profile = await loginWithEmail(email, password);
      setUserProfile(profile);
    } catch (err: any) {
      const message = err.code === 'auth/invalid-credential' 
        ? 'Invalid email or password'
        : err.message || 'Failed to log in';
      setError(message);
      throw err;
    }
  };

  const startGoogleLogin = async (): Promise<{ needsRole: boolean }> => {
    try {
      setError(null);
      const result = await startGoogleSignIn();
      if (result.needsRole) {
        setPendingGoogleRole(true);
      }
      return result;
    } catch (err: any) {
      // Don't show error for popup closed by user - they can just try again
      if (err.code === 'auth/popup-closed-by-user' ||
          err.code === 'auth/cancelled-popup-request' ||
          err.code === 'auth/popup-blocked') {
        // Silent fail - user can retry
        throw err;
      }
      const message = err.message || 'Failed to sign in with Google';
      setError(message);
      throw err;
    }
  };

  const completeGoogleLogin = async (role: 'teacher' | 'student'): Promise<void> => {
    try {
      setError(null);
      const profile = await completeGoogleSignIn(role);
      setUserProfile(profile);
      setPendingGoogleRole(false);
      // Same event, other route. Instrumenting the register form would miss this one entirely:
      // Google takes the user off the page and the account is created on the way back.
      trackEvent('sign_up', { method: 'google', user_role: role });
      if (role === 'teacher') {
        // Teachers only. A student account never emits an advertising event —
        // the tag is not even loaded for them (see initAnalytics).
        trackMetaEvent('CompleteRegistration', { content_name: 'teacher_account', status: true });
        if (profile.subscription.status === 'trialing') {
          trackMetaEvent('StartTrial', { content_name: 'automatic_trial', predicted_ltv: 0, currency: 'CAD' });
        }
      }
    } catch (err: any) {
      const message = err.message || 'Failed to complete Google sign in';
      setError(message);
      throw err;
    }
  };

  const register = async (
    email: string,
    password: string,
    role: 'teacher' | 'student' | 'board_admin',
    displayName: string
  ) => {
    try {
      setError(null);
      const profile = await registerWithEmail(email, password, role, displayName);
      setUserProfile(profile);
      /**
       * GA4's `sign_up`, which is the whole reason the Analytics property reports zero key events.
       *
       * Meta was already told about every registration here; GA4 never was. 2,250 events recorded in
       * a month and not one of them a signup, so the only question that matters — do the people
       * arriving from search and ChatGPT become users — was unanswerable from the reports.
       *
       * `sign_up` is GA4's own recommended name, so it appears in the interface without custom
       * definitions and can be marked a key event in two clicks. `method` separates the two routes,
       * because they convert at different rates and the Google one leaves the page entirely.
       *
       * Emitted for every role, unlike the Meta events above: a student signup is not an advertising
       * event but it IS a signup, and a funnel that silently omits them misreports the product.
       */
      trackEvent('sign_up', { method: 'password', user_role: role });
      if (role === 'teacher') {
        // Teachers only. A student account never emits an advertising event —
        // the tag is not even loaded for them (see initAnalytics).
        trackMetaEvent('CompleteRegistration', { content_name: 'teacher_account', status: true });
        if (profile.subscription.status === 'trialing') {
          trackMetaEvent('StartTrial', { content_name: 'automatic_trial', predicted_ltv: 0, currency: 'CAD' });
        }
      }
    } catch (err: any) {
      let message = 'Failed to create account';
      if (err.code === 'auth/email-already-in-use') {
        message = 'An account with this email already exists';
      } else if (err.code === 'auth/weak-password') {
        message = 'Password should be at least 6 characters';
      } else if (err.code === 'auth/invalid-email') {
        message = 'Invalid email address';
      }
      setError(message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await authLogout();
      setUserProfile(null);
    } catch (err: any) {
      setError(err.message || 'Failed to log out');
      throw err;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      setError(null);
      await resetPassword(email);
    } catch (err: any) {
      const message = err.code === 'auth/user-not-found'
        ? 'No account found with this email'
        : err.message || 'Failed to send reset email';
      setError(message);
      throw err;
    }
  };

  const updateSettings = async (settings: Partial<UserProfile['settings']>) => {
    if (!currentUser) throw new Error('Not authenticated');
    
    try {
      await updateUserSettings(currentUser.uid, settings);
      await refreshProfile();
    } catch (err: any) {
      setError(err.message || 'Failed to update settings');
      throw err;
    }
  };

  const canUseFeature = (feature: keyof typeof PLAN_LIMITS.free): boolean => {
    if (!userProfile) return false;

    // Enterprise teachers covered by their school's active plan — full access
    if (isEnterpriseTeacher) return true;

    const plan = getEffectivePlan(userProfile);
    const limits = PLAN_LIMITS[plan];

    const value = limits[feature];

    // Boolean features
    if (typeof value === 'boolean') {
      return value;
    }

    // Numeric features (check if under limit)
    return true; // Actual limit checking happens at usage time
  };

  const clearError = () => setError(null);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    pendingGoogleRole,
    error,
    isEnterpriseTeacher,
    login,
    startGoogleLogin,
    completeGoogleLogin,
    register,
    logout,
    forgotPassword,
    updateSettings,
    canUseFeature,
    refreshProfile,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
